import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { eur } from "@/lib/gastos";
import { Avatar } from "@/components/personas/Avatar";

export const SIN_ASIGNAR = "__sin_asignar__";
export const dePersona = (g, persona) => !persona || (persona === SIN_ASIGNAR ? !g.family_member_id : g.family_member_id === persona);

const chip = (sel) => `shrink-0 min-h-[52px] rounded-full text-base font-semibold border-2 inline-flex items-center gap-2 ${sel ? "bg-[#1C2421] border-[#1C2421] text-white" : "bg-white border-[#E5DFD5] text-[#1C2421]"}`;

// Filtro principal: Todos · personas · Sin asignar.
export const SelectorPersona = ({ value, onChange }) => {
  const { familyMembers = [] } = useData();
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="radiogroup" aria-label="Persona" data-testid="gastos-personas">
      <button role="radio" aria-checked={!value} data-testid="gastos-persona-todos" onClick={() => onChange("")} className={`${chip(!value)} px-5`}>Todos</button>
      {familyMembers.map((p) => (
        <button key={p.id} role="radio" aria-checked={value === p.id} data-testid={`gastos-persona-${p.id}`} onClick={() => onChange(p.id)} className={`${chip(value === p.id)} pl-1.5 pr-4`}>
          <Avatar persona={p} size="sm" className={value === p.id ? "ring-2 ring-white" : ""} /> <span aria-hidden="true">{p.name}</span>
        </button>
      ))}
      <button role="radio" aria-checked={value === SIN_ASIGNAR} data-testid="gastos-persona-sin" onClick={() => onChange(SIN_ASIGNAR)} className={`${chip(value === SIN_ASIGNAR)} px-5`}>Sin asignar</button>
    </div>
  );
};

// Vista Todos: total del periodo por persona (pulsable).
export const ResumenPersonas = ({ gastos, onElegir }) => {
  const { familyMembers = [] } = useData();
  const filas = familyMembers.map((p) => [p, gastos.filter((g) => g.family_member_id === p.id).reduce((s, g) => s + g.importe, 0)]).filter(([, t]) => t > 0);
  const sin = gastos.filter((g) => !familyMembers.some((p) => p.id === g.family_member_id)).reduce((s, g) => s + g.importe, 0);
  if (!filas.length && !sin) return null;
  return (
    <ul className="mt-4 pt-4 border-t border-[#EFEAE1] space-y-1" data-testid="gastos-por-persona">
      {filas.map(([p, t]) => (
        <li key={p.id}><button data-testid={`gastos-por-persona-${p.id}`} onClick={() => onElegir(p.id)} className="w-full min-h-[44px] flex items-center gap-2.5 text-base text-left">
          <Avatar persona={p} size="sm" /><span className="flex-1 text-[#1C2421]" aria-hidden="true">{p.name}</span><span className="font-bold">{eur(t)}</span></button></li>
      ))}
      {sin > 0 && (
        <li><button data-testid="gastos-por-persona-sin" onClick={() => onElegir(SIN_ASIGNAR)} className="w-full min-h-[44px] flex items-center gap-2.5 text-base text-left text-[#6E7A74]">
          <span className="w-8 h-8 rounded-full border-2 border-dashed border-[#C9C1B3] shrink-0" /><span className="flex-1">Sin asignar</span><span className="font-bold">{eur(sin)}</span></button></li>
      )}
    </ul>
  );
};

export const camposGasto = (g) => ({
  fecha: g.fecha, concepto: g.concepto, proveedor: g.proveedor, categoria: g.categoria, importe: g.importe, notas: g.notas,
  vivienda_id: g.vivienda_id, vehiculo_id: g.vehiculo_id, pet_id: g.pet_id, documento_id: g.documento_id, forma_pago: g.forma_pago, impuestos: g.impuestos,
});

// Asignar persona con un toque, sin abrir la edición.
export const AsignarRapido = ({ g, testid }) => {
  const { familyMembers = [], guardarGasto } = useData();
  const [busy, setBusy] = useState(false);
  const asignar = async (p) => {
    setBusy(true);
    try { await guardarGasto(g.id, { ...camposGasto(g), family_member_id: p.id }); toast.success(`Asignado a ${p.name}`); }
    catch { toast.error("No se pudo asignar."); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="text-sm font-semibold text-[#4A5550]">Asignar a:</span>
      {familyMembers.map((p) => (
        <button key={p.id} disabled={busy} data-testid={`${testid}-${p.id}`} onClick={() => asignar(p)}
          className="min-h-[44px] pl-1 pr-3 rounded-full bg-white border border-[#E5DFD5] inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-60">
          <Avatar persona={p} size="sm" /> <span aria-hidden="true">{p.name}</span>
        </button>
      ))}
    </div>
  );
};


// Asociar vivienda, vehículo o mascota con un toque (solo si el gasto aún no tiene ninguna).
export const AsociarRapido = ({ g, testid }) => {
  const { viviendas = [], vehiculos = [], pets = [], guardarGasto } = useData();
  const [busy, setBusy] = useState(false);
  const items = [
    ...viviendas.map((v) => ({ k: `vivienda-${v.id}`, campo: "vivienda_id", id: v.id, label: v.alias, Icon: Icons.Home })),
    ...vehiculos.map((v) => ({ k: `vehiculo-${v.id}`, campo: "vehiculo_id", id: v.id, label: v.alias, Icon: Icons.Car })),
    ...pets.map((p) => ({ k: `mascota-${p.id}`, campo: "pet_id", id: p.id, label: p.name, Icon: Icons.PawPrint })),
  ];
  if (!items.length) return null;
  const asociar = async (it) => {
    setBusy(true);
    try { await guardarGasto(g.id, { ...camposGasto(g), vivienda_id: null, vehiculo_id: null, pet_id: null, [it.campo]: it.id }); toast.success(`Asociado a ${it.label}`); }
    catch { toast.error("No se pudo asociar."); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="text-sm font-semibold text-[#4A5550]">Asociar a:</span>
      {items.map((it) => (
        <button key={it.k} disabled={busy} data-testid={`${testid}-${it.k}`} onClick={() => asociar(it)}
          className="min-h-[44px] px-3 rounded-full bg-white border border-[#E5DFD5] inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-60">
          <it.Icon size={16} className="text-[#3A6B56]" /> {it.label}
        </button>
      ))}
    </div>
  );
};
